/**
 * How the import page cuts a queue of files into requests.
 *
 * Svelte-free by design, exactly so it can be exercised from the backend test
 * suite — the same arrangement as ./correction.js.
 *
 * Two limits, not one. The server caps a batch at 200 files and the whole body
 * at 50 MB, and a signed `.p7m` travels as base64: 50 files of 1 MB each — the
 * per-file cap, which an invoice with a PDF allegato reaches routinely — is
 * about 67 MB on the wire. The whole chunk then 413s and every file in it is
 * reported failed, none of them for a reason that is about the file.
 */

/** Files per request. Well under the server's 200. */
export const MAX_FILES_PER_BATCH = 50;

/**
 * Encoded bytes per request. Two thirds of the server's 50 MB body cap, leaving
 * room for the JSON envelope, the filenames and the base64 padding.
 */
export const MAX_ENCODED_BYTES_PER_BATCH = 32 * 1024 * 1024;

/** What a file costs on the wire: base64 is four bytes for every three. */
export function encodedSize(item) {
  const size = Number(item?.size) || 0;
  return item?.signed ? Math.ceil(size / 3) * 4 : size;
}

/**
 * Split `items` into batches that satisfy both limits.
 *
 * A single file larger than the byte budget still gets its own batch: the
 * server is the one entitled to refuse it, with a reason naming the file.
 *
 * @param {Array<{size?: number, signed?: boolean}>} items
 * @param {object} [limits]
 * @returns {Array<Array<object>>}
 */
export function planImportBatches(items, {
  maxFiles = MAX_FILES_PER_BATCH,
  maxEncodedBytes = MAX_ENCODED_BYTES_PER_BATCH,
} = {}) {
  const batches = [];
  let current = [];
  let bytes = 0;

  for (const item of items) {
    const cost = encodedSize(item);
    const full = current.length >= maxFiles
      || (current.length > 0 && bytes + cost > maxEncodedBytes);
    if (full) {
      batches.push(current);
      current = [];
      bytes = 0;
    }
    current.push(item);
    bytes += cost;
  }

  if (current.length) batches.push(current);
  return batches;
}
